(() => {
  const CART_PAGE_NAME = "cart-page";

  if (customElements.get(CART_PAGE_NAME)) return;

  const CONTENT_SELECTOR = "[data-cart-content]";

  class CartPage extends HTMLElement {
    #abortController = null;
    #refreshTail = Promise.resolve();

    connectedCallback() {
      this.#abortController = new AbortController();

      // Delegated: quantity, remove and upsell controls are all replaced
      // wholesale on every cart update, and the upsell cards arrive later
      // from the Product Recommendations API.
      this.addEventListener("click", (event) => this.#onClick(event), {
        signal: this.#abortController.signal,
      });

      // The gift controller asks its host surface to re-render after adding or
      // removing a reward line. Claiming the event keeps it from falling back
      // to a full page reload.
      document.addEventListener("fgwp:refresh", this.#handleGiftRefresh, {
        signal: this.#abortController.signal,
      });

      // The drawer is on this page too, showing the same bag. A change made
      // there has to re-render these rows, or they linger at quantity 0.
      document.addEventListener("cart:updated", this.#handleExternalUpdate, {
        signal: this.#abortController.signal,
      });
    }

    #handleGiftRefresh = (event) => {
      event.preventDefault();
      // Deliberately not followed by cart:updated — that would send the
      // controller round again for a change it made itself.
      this.#refresh();
    };

    #handleExternalUpdate = (event) => {
      if (event.detail?.source === "page") return;

      this.#refresh();
    };

    /**
     * Tell the gift controller the cart moved. It cannot observe
     * /cart/change.js from here, so a threshold crossed by a quantity change
     * would otherwise never add or remove a reward line.
     */
    #announceCartUpdate() {
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { source: "page" },
        }),
      );
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
    }

    #onClick(event) {
      const minus = event.target.closest("[data-cart-quantity-minus]");
      if (minus) {
        this.#changeQuantity(minus, -1);
        return;
      }

      const plus = event.target.closest("[data-cart-quantity-plus]");
      if (plus) {
        this.#changeQuantity(plus, 1);
        return;
      }

      const remove = event.target.closest("[data-cart-remove]");
      if (remove) {
        this.#removeLine(remove);
        return;
      }

      // Upsell size selection is handled by assets/cart-upsell.js, which the
      // cart drawer also loads.
      const add = event.target.closest("[data-cart-upsell-add]");
      if (add) {
        this.#addUpsell(add);
      }
    }

    async #changeQuantity(button, delta) {
      await CartLineItems.changeQuantity(
        button,
        delta,
        (lineItem, quantity) => this.#updateLine(lineItem, quantity),
        "page",
      );
    }

    async #removeLine(button) {
      await CartLineItems.removeLine(
        button,
        (lineItem, quantity) => this.#updateLine(lineItem, quantity),
        "page",
      );
    }

    /**
     * Runs inside the shared per-line queue, so repeat clicks coalesce into
     * one request rather than racing `/cart/change.js`.
     */
    async #updateLine(lineItem, quantity) {
      const lineKey = lineItem.dataset.lineKey;

      if (!lineKey) {
        throw new Error("Cart update failed");
      }

      this.#clearError();
      lineItem.setAttribute("aria-busy", "true");
      this.#setSummaryBusy(true);

      try {
        const response = await CartLineItems.requestLineQuantityChange(
          lineKey,
          quantity,
          this.dataset.sectionId,
        );

        if (!response.ok) {
          throw new Error("Cart update failed");
        }

        const cart = await response.json();
        await this.#refresh(cart.sections?.[this.dataset.sectionId]);
        this.#announceCartUpdate();

        const line = cart.items?.find((item) => item.key === lineKey);
        return line ? line.quantity : 0;
      } catch (error) {
        const activeLineItem =
          this.querySelector(
            `[data-cart-line-item][data-line-key="${CSS.escape(lineKey)}"]`,
          ) ?? lineItem;

        if (activeLineItem.isConnected) {
          activeLineItem.removeAttribute("aria-busy");
        }

        this.#setSummaryBusy(false);
        this.#showError(this.dataset.errorUpdate);
        throw error;
      }
    }

    async #addUpsell(button) {
      if (button.disabled) return;

      const card = button.closest("[data-cart-upsell-card]");
      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      this.#clearError();
      button.disabled = true;
      card?.setAttribute("aria-busy", "true");

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: this.dataset.sectionId,
          }),
        });

        if (!response.ok) {
          throw new Error("Add to cart failed");
        }

        const payload = await response.json();
        await this.#refresh(payload.sections?.[this.dataset.sectionId]);
        button.closest("cart-upsell")?.dropCard?.(button);
        this.#announceCartUpdate();
      } catch {
        card?.removeAttribute("aria-busy");
        button.disabled = false;
        this.#showError(this.dataset.errorAdd);
      }
    }

    /**
     * Swaps the rendered cart. Serialised so two quick updates cannot land
     * out of order, and falls back to a fresh section request when the change
     * response carried no section markup.
     */
    #refresh(sectionMarkup) {
      const task = async () => {
        let markup = sectionMarkup;

        if (!markup) {
          const url = new URL(window.location.href);
          url.searchParams.set("section_id", this.dataset.sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) return;

          markup = await response.text();
        }

        const parsed = new DOMParser().parseFromString(markup, "text/html");
        const incoming = parsed.querySelector(CONTENT_SELECTOR);
        const current = this.querySelector(CONTENT_SELECTOR);

        if (!incoming || !current) return;

        // Same first item, same recommendations: keep the carousel that is
        // already on screen rather than fetching it again (the blink the
        // review saw on every quantity change).
        customElements.get("cart-upsell")?.adoptLive?.(current, incoming);
        current.replaceWith(incoming);
        this.#updateHeaderCart(incoming.dataset.itemCount);
      };

      const next = this.#refreshTail.catch(() => {}).then(task);
      this.#refreshTail = next;
      return next;
    }

    #setSummaryBusy(isBusy) {
      for (const node of this.querySelectorAll(
        "[data-cart-summary], [data-cart-sticky-checkout]",
      )) {
        if (isBusy) {
          node.setAttribute("aria-busy", "true");
        } else {
          node.removeAttribute("aria-busy");
        }
      }
    }

    #showError(message) {
      const node = this.querySelector("[data-cart-error]");
      if (!node || !message) return;

      node.textContent = message;
      node.hidden = false;
    }

    #clearError() {
      const node = this.querySelector("[data-cart-error]");
      if (!node) return;

      node.textContent = "";
      node.hidden = true;
    }

    #updateHeaderCart(itemCount) {
      const count = Number(itemCount || 0);

      for (const trigger of document.querySelectorAll(
        "[data-cart-drawer-open]",
      )) {
        const badge = trigger.querySelector("[data-cart-count-badge]");
        const label = trigger.querySelector("[data-cart-trigger-label]");

        if (badge) {
          badge.hidden = count === 0;
          badge.textContent = String(count);
        }

        if (label) {
          const cartLabel = trigger.dataset.cartLabel;
          if (!cartLabel) continue;

          label.textContent = count > 0 ? `${cartLabel} (${count})` : cartLabel;
        }
      }
    }
  }

  customElements.define(CART_PAGE_NAME, CartPage);
})();
